import type {
  AdversaireDTO, Approche, ApprocheDTO, AtelierDTO,
  BoutiqueDTO, CarteDTO, CombatDTO, CompteDTO,
  ConfrerieDTO, DetailLieuDTO, DonjonDTO, Emplacement, EnteteDTO,
  EnvoiDTO, ExpeditionDTO, GensDTO, MarcheDTO,
  MouvementRenomDTO, ObjetDTO, OptionsBesogneDTO, OptionsCreationDTO, PersonnageDTO, PoseDTO,
  QueteDTO, ReputationDTO, SeuilDTO, Stat, TitreEnMainDTO,
} from '@whatremains/shared'

/**
 * Le client parle au serveur, et ne calcule rien.
 *
 * Aucune règle de jeu n'est reproduite ici : ni dégâts, ni seuils, ni prérequis.
 * Tout ce qui s'affiche vient d'une réponse. Une règle recopiée côté navigateur
 * serait une règle qu'on peut contredire.
 */

export class ErreurApi extends Error {
  constructor(
    readonly statut: number,
    message: string,
  ) {
    super(message)
  }
}

async function appel<T>(chemin: string, init?: RequestInit): Promise<T> {
  const reponse = await fetch(chemin, {
    credentials: 'include',
    headers: init?.body ? { 'content-type': 'application/json' } : undefined,
    ...init,
  })

  if (reponse.status === 204) return undefined as T

  const corps = (await reponse.json().catch(() => null)) as { erreur?: string } | null
  if (!reponse.ok) {
    throw new ErreurApi(reponse.status, corps?.erreur ?? "Quelque chose n'a pas abouti.")
  }
  return corps as T
}

const poste = <T>(chemin: string, donnees?: unknown) =>
  appel<T>(chemin, { method: 'POST', ...(donnees ? { body: JSON.stringify(donnees) } : {}) })

export interface Resume {
  compte: CompteDTO
  personnages: { id: string; nom: string; classe: string; niveau: number }[]
}

export const api = {
  moi: () => appel<Resume>('/api/moi'),
  inscription: (email: string, motDePasse: string) =>
    poste<CompteDTO>('/api/inscription', { email, motDePasse }),
  connexion: (email: string, motDePasse: string) =>
    poste<CompteDTO>('/api/connexion', { email, motDePasse }),
  deconnexion: () => poste<void>('/api/deconnexion'),

  optionsCreation: () => appel<OptionsCreationDTO>('/api/creation'),
  creer: (donnees: { nom: string; classe: string; origine: string; penchant: string[] }) =>
    poste<PersonnageDTO>('/api/personnages', donnees),

  personnage: (id: string) => appel<PersonnageDTO>(`/api/personnages/${id}`),
  inventaire: (id: string) => appel<ObjetDTO[]>(`/api/personnages/${id}/inventaire`),
  repartir: (id: string, points: Partial<Record<Stat, number>>) =>
    poste<PersonnageDTO>(`/api/personnages/${id}/statistiques`, points),
  seuil: (id: string) => appel<SeuilDTO | null>(`/api/personnages/${id}/seuil`),
  poserArtefact: (id: string) =>
    poste<{ texte: string }>(`/api/personnages/${id}/artefact/poser`, {}),
  survenueArtefactVue: (id: string) =>
    poste<{ vu: boolean }>(`/api/personnages/${id}/artefact/survenue-vue`, {}),
  pose: (id: string) => appel<PoseDTO | null>(`/api/personnages/${id}/pose`),
  prendrePose: (id: string) => poste<{ pris: boolean }>(`/api/personnages/${id}/pose/prendre`, {}),
  consentir: (id: string) =>
    poste<{ pris: boolean }>(`/api/personnages/${id}/seuil/consentir`, {}),

  equiper: (id: string, objetId: string, emplacement?: Emplacement) =>
    poste<PersonnageDTO>(`/api/personnages/${id}/equiper`, { objetId, emplacement }),
  ranger: (id: string, objetId: string) =>
    poste<PersonnageDTO>(`/api/personnages/${id}/ranger`, { objetId }),

  approches: () => appel<ApprocheDTO[]>('/api/approches'),
  /**
   * Le bestiaire tel que CE personnage le rencontrera, échelle de quête comprise.
   *
   * La route globale annonce le niveau du bestiaire ; une quête remise à
   * l'échelle fait monter l'adversaire qu'elle réclame. Lire la globale
   * revenait à afficher un adversaire et à en combattre un autre.
   */
  bestiaire: (id: string) => appel<RegionAdversaires[]>(`/api/personnages/${id}/adversaires`),
  combatEnCours: (id: string) => appel<CombatDTO>(`/api/personnages/${id}/combat`),
  engager: (id: string, adversaire: string, approche: Approche, interactif: boolean) =>
    poste<CombatDTO>(`/api/personnages/${id}/combat`, { adversaire, approche, interactif }),
  agir: (id: string, competence: string) =>
    poste<CombatDTO>(`/api/personnages/${id}/combat/agir`, { competence }),
  laisserFiler: (id: string) => poste<CombatDTO>(`/api/personnages/${id}/combat/laisser-filer`),

  entete: (id: string) => appel<EnteteDTO>(`/api/personnages/${id}/entete`),
  carte: (id: string) => appel<CarteDTO>(`/api/personnages/${id}/carte`),
  voyager: (id: string, lieu: string) =>
    poste<{
      lieu: string; nom: string; region: string; coutPaye: number; premiereFois: boolean
      /** Ce qu'on aperçoit, s'il y a quelque chose. Rien n'explique quoi. */
      apercu?: string | null
    }>(
      `/api/personnages/${id}/voyage`,
      { lieu },
    ),
  reputations: (id: string) => appel<ReputationDTO[]>(`/api/personnages/${id}/reputations`),
  mouvementsRenom: (id: string) =>
    appel<MouvementRenomDTO[]>(`/api/personnages/${id}/renom/mouvements`),
  lieu: (id: string, code: string) => appel<DetailLieuDTO>(`/api/personnages/${id}/lieux/${code}`),

  boutique: (id: string) => appel<BoutiqueDTO>(`/api/personnages/${id}/boutique`),
  acheter: (id: string, ligneId: string) =>
    poste<{ prix: number }>(`/api/personnages/${id}/boutique/acheter`, { ligneId }),
  /** Vendre au marchand. `quantite` vend tout un lot d'un coup. */
  vendre: (id: string, objetId: string, quantite = 1) =>
    poste<{ prix: number; quantite: number; prixUnite: number }>(
      `/api/personnages/${id}/boutique/vendre`, { objetId, quantite },
    ),

  /** Ouvrir une région, une fois, contre de l'or. Quatrième puits d'or du canon. */
  ouvrirRegion: (id: string, region: string) =>
    poste<{ ouverte: boolean; paye: number; nom: string }>(
      `/api/personnages/${id}/regions/${region}/ouvrir`, {},
    ),
  marche: (id: string) => appel<MarcheDTO>(`/api/personnages/${id}/marche`),
  /**
   * Ce que le dépôt donnerait, pour les trois durées, avant de le faire.
   *
   * Le serveur calcule, comme tout le reste : la règle 2 ne se contourne pas
   * pour un affichage.
   */
  estimerDepot: (id: string, objetId: string, prix: number, quantite = 1) =>
    poste<{ frais: number; total: number; chances: { minutes: number; chance: number }[] }>(
      `/api/personnages/${id}/marche/estimer`,
      { objetId, prix, quantite },
    ),
  /** `prix` est UNITAIRE. Le total vaut `prix × quantite`. */
  deposer: (id: string, objetId: string, prix: number, dureeMinutes: number, quantite = 1) =>
    poste<{ id: string; frais: number; chance: number; quantite: number; total: number }>(
      `/api/personnages/${id}/marche/deposer`,
      { objetId, prix, dureeMinutes, quantite },
    ),
  /** Relever un invendu ET le remettre en vente, en une seule transaction. */
  relancerOrdre: (id: string, ordreId: string, prix?: number, dureeMinutes?: number) =>
    poste<{ id: string; frais: number; chance: number; quantite: number; total: number }>(
      `/api/personnages/${id}/marche/relancer`,
      { ordreId, ...(prix !== undefined ? { prix } : {}), ...(dureeMinutes !== undefined ? { dureeMinutes } : {}) },
    ),
  releverOrdre: (id: string, ordreId: string) =>
    poste<{ vendu: boolean; or: number; quantite: number }>(
      `/api/personnages/${id}/marche/relever`, { ordreId },
    ),
  acheterEtal: (id: string, etalId: string) =>
    poste<{ prix: number }>(`/api/personnages/${id}/marche/acheter`, { etalId }),

  donjons: (id: string) => appel<DonjonDTO[]>(`/api/personnages/${id}/donjons`),
  entrerDonjon: (id: string, code: string) =>
    poste<{ id: string }>(`/api/personnages/${id}/donjons/${code}/entrer`),
  expedition: (id: string) => appel<ExpeditionDTO | null>(`/api/personnages/${id}/expedition`),
  avancerEtage: (id: string, choix?: string) =>
    poste<Record<string, unknown>>(
      `/api/personnages/${id}/expedition/avancer`,
      choix ? { choix } : {},
    ),
  ressortir: (id: string) =>
    poste<{ or: number; pieces: number }>(`/api/personnages/${id}/expedition/ressortir`),

  atelier: (id: string) => appel<AtelierDTO>(`/api/personnages/${id}/atelier`),
  ameliorer: (id: string, objetId: string) =>
    poste<{ cran: number; or: number; matiere: number }>(
      `/api/personnages/${id}/atelier/ameliorer`, { objetId },
    ),
  detruire: (id: string, objetId: string) =>
    poste<{ matiere: string; nombre: number }>(
      `/api/personnages/${id}/atelier/detruire`, { objetId },
    ),
  consommer: (id: string, objetId: string) =>
    poste<{ nom: string; rendu: number }>(
      `/api/personnages/${id}/atelier/consommer`, { objetId },
    ),

  confreries: (id: string) => appel<ConfrerieDTO[]>(`/api/personnages/${id}/confreries`),

  /*
   * Les titres. Les trois routes existaient depuis le début et le client n'en
   * appelait AUCUNE : le système était complet côté serveur, invisible côté
   * joueur, et rien ne pouvait en accorder un de toute façon.
   */
  titres: (id: string) => appel<TitreEnMainDTO[]>(`/api/personnages/${id}/titres`),
  arborer: (id: string, titre: string | null) =>
    poste<{ arbore: string | null }>(`/api/personnages/${id}/titres/arborer`, { titre }),
  detruireTitre: (id: string, code: string) =>
    poste<{ detruit: string }>(`/api/personnages/${id}/titres/${code}/detruire`, {}),
  acheterConfrerie: (id: string, code: string, modeleCode: string) =>
    poste<{ prix: number }>(`/api/personnages/${id}/confreries/${code}/acheter`, { modeleCode }),

  gens: (id: string) => appel<GensDTO>(`/api/personnages/${id}/gens`),
  prendreOffre: (id: string, offre: string) =>
    poste<{
      qui: string; issue: string; texte: string; paye: number; obtenu: string[]
      /** Le Débiteur a parlé. Une fois, et rien de plus ne sera dit. */
      debiteur?: boolean
    }>(`/api/personnages/${id}/offres/prendre`, { offre }),
  repondreAuDebiteur: (id: string, accepte: boolean) =>
    poste<{ pris: boolean }>(`/api/personnages/${id}/debiteur`, { accepte }),

  quetesIci: (id: string) => appel<QueteDTO[]>(`/api/personnages/${id}/quetes/ici`),
  journal: (id: string) => appel<QueteDTO[]>(`/api/personnages/${id}/quetes`),
  accepterQuete: (id: string, code: string) =>
    poste<QueteDTO>(`/api/personnages/${id}/quetes/${code}/accepter`),
  avancerQuete: (id: string, code: string, choix: string | null) =>
    poste<QueteDTO>(`/api/personnages/${id}/quetes/${code}/avancer`, choix ? { choix } : {}),
  abandonnerQuete: (id: string, code: string) =>
    poste<void>(`/api/personnages/${id}/quetes/${code}/abandonner`),

  besognes: (id: string) => appel<OptionsBesogneDTO>(`/api/personnages/${id}/besognes`),
  envois: (id: string) => appel<EnvoiDTO[]>(`/api/personnages/${id}/envois`),
  envoyer: (id: string, region: string, besogne: string, duree: number) =>
    poste<EnvoiDTO>(`/api/personnages/${id}/besognes`, { region, besogne, duree }),
  relever: (id: string, envoiId: string) =>
    poste<EnvoiDTO>(`/api/personnages/${id}/envois/${envoiId}/relever`),
}

export interface RegionAdversaires {
  code: string
  nom: string
  seuilNiveau: number
  adversaires: AdversaireDTO[]
}
